import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { OwnerViewProvider, useOwnerView } from './context/OwnerViewContext';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import OnboardingTour from './components/OnboardingTour';
import AdminSetupWizard from './components/AdminSetupWizard';

// Pages (Lazy Loaded)
const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CaseList = React.lazy(() => import('./pages/CaseList'));
const CaseDetail = React.lazy(() => import('./pages/CaseDetail'));
const CreateCase = React.lazy(() => import('./pages/CreateCase'));
const EvidenceList = React.lazy(() => import('./pages/EvidenceList'));
const EvidenceDetail = React.lazy(() => import('./pages/EvidenceDetail'));
const DocumentList = React.lazy(() => import('./pages/DocumentList'));
const PersonnelList = React.lazy(() => import('./pages/PersonnelList'));
const AuditLogView = React.lazy(() => import('./pages/AuditLogView'));
const Profile = React.lazy(() => import('./pages/Profile'));
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const MapPage = React.lazy(() => import('./pages/MapPage'));
const CaseRequests = React.lazy(() => import('./pages/CaseRequests'));
const Billing = React.lazy(() => import('./pages/admin/Billing'));
const Compliance = React.lazy(() => import('./pages/Compliance'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const OwnerDashboard = React.lazy(() => import('./pages/OwnerDashboard'));
const BulkExport = React.lazy(() => import('./pages/BulkExport'));
const ScheduledReports = React.lazy(() => import('./pages/ScheduledReports'));

// CJIS 5.12: Screen lock required after 30 minutes of inactivity.
// Show a 2-minute warning after 28 minutes of inactivity, then auto-logout.
const INACTIVITY_WARN_MS = 28 * 60 * 1000;  // 28 minutes idle → show warning
const SESSION_TIMEOUT_MS = INACTIVITY_WARN_MS;

function AppContent() {
  const { user, logout, updateUser, loading: authLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);
  const isMobile = () => window.innerWidth <= 768;
  const isDa = user?.role === 'da';
  const isOwner = user?.role === 'owner';
  const isOwnerPreview = !!user?.ownerPreview;
  const { viewMode, viewAgency, exitViewMode } = useOwnerView();

  // ── Forced password change modal ──────────────────────────────────────────
  const [showForceChange, setShowForceChange] = useState(false);
  const [forceOldPw, setForceOldPw] = useState('');
  const [forceNewPw, setForceNewPw] = useState('');
  const [forceConfirm, setForceConfirm] = useState('');
  const [forceSaving, setForceSaving] = useState(false);
  const [forceError, setForceError] = useState('');

  useEffect(() => {
    if (user?.mustChangePassword) setShowForceChange(true);
  }, [user?.mustChangePassword]);

  const handleForceChange = async (e) => {
    e.preventDefault();
    setForceError('');
    if (forceNewPw !== forceConfirm) { setForceError('Passwords do not match'); return; }
    if (forceNewPw.length < 12) { setForceError('Password must be at least 12 characters'); return; }
    try {
      setForceSaving(true);
      const api = (await import('./services/api')).default;
      await api.put('/auth/updatepassword', { currentPassword: forceOldPw, newPassword: forceNewPw });
      // Clear the mustChangePassword flag from user state so modal doesn't re-appear
      if (updateUser && user) updateUser({ ...user, mustChangePassword: false });
      setShowForceChange(false);
      setForceOldPw(''); setForceNewPw(''); setForceConfirm('');
    } catch (err) {
      setForceError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setForceSaving(false);
    }
  };

  // ── Password expiry warning banner (CJIS 5.6) ─────────────────────────────
  // Show a dismissible banner when the password expires within 14 days.
  const [showPwExpiryBanner, setShowPwExpiryBanner] = useState(false);
  const [pwExpiryDays, setPwExpiryDays] = useState(null);
  useEffect(() => {
    if (!user?.passwordExpiresAt) return;
    const msLeft = new Date(user.passwordExpiresAt).getTime() - Date.now();
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
    if (daysLeft > 0 && daysLeft <= 14) {
      setPwExpiryDays(daysLeft);
      setShowPwExpiryBanner(true);
    } else {
      setShowPwExpiryBanner(false);
    }
  }, [user?.passwordExpiresAt]);

  // ── Session timeout warning ───────────────────────────────────────────────
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutSecsLeft, setTimeoutSecsLeft] = useState(120);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);

  const resetSessionTimer = useCallback(() => {
    if (!user) return;
    setShowTimeoutWarning(false);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);
    warningRef.current = setTimeout(() => {
      setTimeoutSecsLeft(120);  // 2-minute countdown before auto-logout
      setShowTimeoutWarning(true);
      countdownRef.current = setInterval(() => {
        setTimeoutSecsLeft(s => {
          if (s <= 1) { clearInterval(countdownRef.current); logout(); return 0; }
          return s - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS);
  }, [user, logout]);

  useEffect(() => {
    if (!user) return;
    resetSessionTimer();
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetSessionTimer));
    const warnId = warningRef.current;
    const countId = countdownRef.current;
    return () => {
      clearTimeout(warnId);
      clearInterval(countId);
      events.forEach(e => window.removeEventListener(e, resetSessionTimer));
    };
  }, [user, resetSessionTimer]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <React.Suspense fallback={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            Loading...
          </div>
        }>
          {authLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0f1e' }} />
          ) : user ? (
            <div className="app">
              {/* ── Owner View Mode Banner ── */}
              {viewMode && viewAgency && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
                  background: 'linear-gradient(90deg, #92400e, #b45309)',
                  borderBottom: '2px solid #f59e0b',
                  padding: '8px 20px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  boxShadow: '0 2px 12px rgba(245,158,11,0.4)'
                }}>
                  <span style={{ color: '#fef3c7', fontWeight: 700, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1rem' }}>👁</span>
                    OWNER VIEW MODE — previewing <strong style={{ color: '#fbbf24' }}>{viewAgency.name}</strong>
                    <span style={{ fontWeight: 400, color: '#fde68a', fontSize: '0.78rem' }}>| Your real session is unchanged</span>
                  </span>
                  <button
                    onClick={() => { exitViewMode(); }}
                    style={{ background: '#92400e', border: '2px solid #f59e0b', color: '#fef3c7', borderRadius: '7px', padding: '5px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    ← Back to Owner Dashboard
                  </button>
                </div>
              )}
              <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} style={{ marginTop: (isOwnerPreview || viewMode) ? '40px' : '0' }} />
              <div className="app-container" style={{ marginTop: (isOwnerPreview || viewMode) ? '40px' : '0' }}>
                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                {sidebarOpen && isMobile() && (
                  <div
                    className="sidebar-backdrop"
                    onClick={() => setSidebarOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998 }}
                  />
                )}
                <main className={`main-content ${sidebarOpen && !isMobile() ? 'with-sidebar' : ''}`}>
                  <Routes>
                    {isOwner && !viewMode ? (
                      <>
                        <Route path="/owner" element={<PrivateRoute><OwnerDashboard /></PrivateRoute>} />
                        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                        <Route path="*" element={<Navigate to="/owner" />} />
                      </>
                    ) : isDa && !viewMode ? (
                      <>
                        <Route path="/requests" element={<PrivateRoute><CaseRequests /></PrivateRoute>} />
                        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="*" element={<Navigate to="/requests" />} />
                      </>
                    ) : (
                      <>
                        <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                        <Route path="/cases" element={<PrivateRoute permission="canCreateCases"><CaseList /></PrivateRoute>} />
                        <Route path="/cases/new" element={<PrivateRoute permission="canCreateCases"><CreateCase /></PrivateRoute>} />
                        <Route path="/cases/:id" element={<PrivateRoute permission="canCreateCases"><CaseDetail /></PrivateRoute>} />
                        <Route path="/evidence" element={<PrivateRoute permission="canManageEvidence"><EvidenceList /></PrivateRoute>} />
                        <Route path="/evidence/:id" element={<PrivateRoute permission="canManageEvidence"><EvidenceDetail /></PrivateRoute>} />
                        <Route path="/documents" element={<PrivateRoute permission="canManageDocuments"><DocumentList /></PrivateRoute>} />
                        <Route path="/personnel" element={<PrivateRoute permission="canManagePersonnel"><PersonnelList /></PrivateRoute>} />
                        <Route path="/timeline" element={<PrivateRoute><AuditLogView /></PrivateRoute>} />
                        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                        <Route path="/analytics" element={<PrivateRoute permission="canViewAnalytics"><AnalyticsDashboard /></PrivateRoute>} />
                        <Route path="/search" element={<PrivateRoute permission="canCreateCases"><SearchPage /></PrivateRoute>} />
                        <Route path="/map" element={<PrivateRoute permission="canCreateCases"><MapPage /></PrivateRoute>} />
                        <Route path="/requests" element={<PrivateRoute><CaseRequests /></PrivateRoute>} />
                        <Route path="/billing" element={<PrivateRoute><Billing /></PrivateRoute>} />
                        <Route path="/compliance" element={<PrivateRoute><Compliance /></PrivateRoute>} />
                        <Route path="/export" element={<PrivateRoute><BulkExport /></PrivateRoute>} />
                        <Route path="/reports" element={<PrivateRoute><ScheduledReports /></PrivateRoute>} />
                        <Route path="/owner" element={<PrivateRoute><OwnerDashboard /></PrivateRoute>} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="*" element={<Navigate to="/" />} />
                      </>
                    )}
                  </Routes>
                </main>
              </div>
              <ToastContainer />
              {/* Onboarding tour for first-time users */}
              {!isOwner && !isDa && <OnboardingTour />}
              {!isOwner && !isDa && <AdminSetupWizard />}

              {/* ── Password Expiry Warning Banner (CJIS 5.6) ── */}
              {showPwExpiryBanner && !showForceChange && (
                <div style={{ position: 'fixed', top: '60px', left: 0, right: 0, zIndex: 9990, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ background: '#78350f', border: '1px solid #f59e0b', borderRadius: '10px', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '12px', maxWidth: '520px', width: '90%', boxShadow: '0 4px 16px rgba(0,0,0,0.4)', pointerEvents: 'all' }}>
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <span style={{ color: '#fcd34d', fontSize: '0.88rem', flex: 1 }}>
                      Your password expires in <strong>{pwExpiryDays} day{pwExpiryDays !== 1 ? 's' : ''}</strong>. Update it in your{' '}
                      <a href="/profile" style={{ color: '#fbbf24', textDecoration: 'underline' }}>Profile</a> to avoid being locked out.
                    </span>
                    <button onClick={() => setShowPwExpiryBanner(false)} style={{ background: 'none', border: 'none', color: '#fcd34d', cursor: 'pointer', fontSize: '1.1rem', padding: '0 4px', lineHeight: 1 }}>✕</button>
                  </div>
                </div>
              )}

              {/* ── Forced Password Change Modal ── */}
              {showForceChange && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                  <div style={{ background: '#1e293b', borderRadius: '14px', padding: '32px', width: '100%', maxWidth: '440px', border: '1px solid #f59e0b' }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                      <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🔐</div>
                      <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.25rem' }}>Password Change Required</h2>
                      <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '8px' }}>You must set a new password before continuing.</p>
                    </div>
                    <form onSubmit={handleForceChange}>
                      {forceError && <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '14px' }}>{forceError}</div>}
                      {[['Temporary Password', forceOldPw, setForceOldPw], ['New Password', forceNewPw, setForceNewPw], ['Confirm New Password', forceConfirm, setForceConfirm]].map(([label, val, setter]) => (
                        <div key={label} style={{ marginBottom: '14px' }}>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '4px' }}>{label}</label>
                          <input type="password" value={val} onChange={e => setter(e.target.value)} required
                            style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                      <div style={{ background: '#1c1917', border: '1px solid #78350f', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.78rem', color: '#fcd34d' }}>
                        Password must be at least 12 characters with uppercase, lowercase, number, and special character.
                      </div>
                      <button type="submit" disabled={forceSaving} style={{ width: '100%', padding: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>
                        {forceSaving ? 'Saving...' : 'Set New Password'}
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* ── Session Timeout Warning ── */}
              {showTimeoutWarning && (
                <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: '#1e293b', border: '1px solid #f59e0b', borderRadius: '12px', padding: '20px 24px', zIndex: 9998, maxWidth: '320px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '1.4rem' }}>⏰</span>
                    <div>
                      <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.95rem' }}>Session Expiring Soon</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>You will be logged out in {Math.floor(timeoutSecsLeft / 60)}:{String(timeoutSecsLeft % 60).padStart(2, '0')}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={resetSessionTimer} style={{ flex: 1, padding: '8px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>Stay Logged In</button>
                    <button onClick={logout} style={{ flex: 1, padding: '8px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Log Out</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          )}

        </React.Suspense>
      </ErrorBoundary>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <OwnerViewProvider>
        <SocketProvider>
          <NotificationProvider>
            <ThemeProvider>
              <AppContent />
            </ThemeProvider>
          </NotificationProvider>
        </SocketProvider>
      </OwnerViewProvider>
    </AuthProvider>
  );
}

export default App;
